import React from 'react';
import PropTypes from 'prop-types';
import styles from './ImportProgress.module.css';

const ImportProgress = ({ importStatus }) => {
  if (!importStatus) return null;

  const {
    status,
    progress,
    timestamps,
    originalFileName,
    hasErrors,
    completionMessage,
  } = importStatus;

  const getStatusColor = (status) => {
    switch (status) {
      case 'Queued':
        return '#f59e0b';
      case 'Running':
        return '#60a5fa';
      case 'Completed':
        return hasErrors ? '#f59e0b' : '#10b981';
      case 'Failed':
        return '#ef4444';
      case 'Cancelled':
        return '#6b7280';
      default:
        return '#60a5fa';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Queued':
        return '⏳';
      case 'Processing':
        return '⚡';
      case 'Completed':
        return hasErrors ? '⚠️' : '✅';
      case 'Failed':
        return '❌';
      case 'Cancelled':
        return '🚫';
      default:
        return '❓';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    let date;

    // Check if it's already a valid date string with timezone info
    if (
      dateString.includes('Z') ||
      dateString.includes('+') ||
      dateString.includes('-', 10)
    ) {
      // Already has timezone info, parse directly
      date = new Date(dateString);
    } else {
      // Server sends UTC time without 'Z' indicator, so add it
      const utcDateString = dateString + 'Z';
      date = new Date(utcDateString);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'N/A';
    }

    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className={styles.progressContainer}>
      <div className={styles.progressHeader}>
        <div className={styles.statusInfo}>
          <span
            className={styles.statusBadge}
            style={{ backgroundColor: getStatusColor(status) }}
          >
            {getStatusIcon(status)} {status}
          </span>
          <span className={styles.fileName}>{originalFileName}</span>
        </div>
        {completionMessage && (
          <div className={styles.completionMessage}>
            🕒{' '}
            {status === 'Completed'
              ? 'Completed:'
              : status === 'Cancelled'
                ? 'Cancelled:'
                : 'Started:'}{' '}
            {formatDate(
              status === 'Completed' || status === 'Cancelled'
                ? timestamps?.completedAt
                : timestamps?.startedAt || timestamps?.createdAt
            )}
          </div>
        )}
      </div>

      {progress && (
        <div className={styles.progressDetails}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{
                width: `${progress.percentComplete}%`,
                backgroundColor: getStatusColor(status),
              }}
            />
            <div className={styles.progressText}>
              {progress.percentComplete}% Complete
            </div>
          </div>

          <div className={styles.statsGrid}>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Total Records</span>
              <span className={styles.statValue}>{progress.totalRecords}</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Processed</span>
              <span className={styles.statValue}>
                {progress.processedRecords}
              </span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Success</span>
              <span className={`${styles.statValue} ${styles.success}`}>
                {progress.successCount}
              </span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Updated</span>
              <span className={`${styles.statValue} ${styles.updated}`}>
                {progress.updatedCount}
              </span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Up to Date</span>
              <span className={`${styles.statValue} ${styles.upToDate}`}>
                {progress.upToDateCount}
              </span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Errors</span>
              <span className={`${styles.statValue} ${styles.errors}`}>
                {progress.errorCount}
              </span>
            </div>
          </div>

          {status === 'Processing' && progress.queuePosition > 0 && (
            <div className={styles.queueInfo}>
              Queue Position: {progress.queuePosition}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

ImportProgress.propTypes = {
  importStatus: PropTypes.shape({
    importJobId: PropTypes.string,
    status: PropTypes.string,
    progress: PropTypes.shape({
      totalRecords: PropTypes.number,
      processedRecords: PropTypes.number,
      successCount: PropTypes.number,
      updatedCount: PropTypes.number,
      upToDateCount: PropTypes.number,
      errorCount: PropTypes.number,
      percentComplete: PropTypes.number,
      queuePosition: PropTypes.number,
    }),
    timestamps: PropTypes.shape({
      createdAt: PropTypes.string,
      startedAt: PropTypes.string,
      completedAt: PropTypes.string,
      duration: PropTypes.string,
    }),
    originalFileName: PropTypes.string,
    hasErrors: PropTypes.bool,
    completionMessage: PropTypes.string,
  }),
};

export default ImportProgress;
